export interface AirRegiTransactionTime {
  storeId?: string;
  visitDate: string | null;
  visitTime: string | null;
  accountDate: string;
  accountTime: string | null;
}

interface ServiceHours {
  lunchStart: string | null;
  lunchAggregationEnd?: string | null;
  dinnerStart: string | null;
  dinnerAggregationEnd?: string | null;
  airregiBusinessDayStart?: string | null;
}

// 両店舗のAirレジ「レジ基本設定」で確認した値（2026-09-21）。店舗マスタ変更前の値にも適用する。
const DEFAULT_DAY_CHANGE_TIME = '05:00';

const minutes = (time: string | null): number | null => {
  const match = /^(\d{1,2}):(\d{2})/.exec(time ?? '');
  if (!match) return null;
  const hour = Number(match[1]);
  const minute = Number(match[2]);
  return hour < 24 && minute < 60 ? hour * 60 + minute : null;
};

const aggregationMinutes = (time: string | null | undefined): number | null => {
  const match = /^(\d{1,2}):([0-5]\d)$/.exec(time ?? '');
  if (!match) return null;
  const hour = Number(match[1]);
  return hour <= 30 ? hour * 60 + Number(match[2]) : null;
};

const addDays = (date: string, days: number): string => {
  const value = new Date(`${date}T00:00:00Z`);
  value.setUTCDate(value.getUTCDate() + days);
  return value.toISOString().slice(0, 10);
};

const daysBetween = (from: string, to: string): number =>
  Math.round((Date.parse(`${to}T00:00:00Z`) - Date.parse(`${from}T00:00:00Z`)) / 86_400_000);

export const airRegiBusinessDate = (
  transaction: AirRegiTransactionTime,
  dayChangeTime: string | null = DEFAULT_DAY_CHANGE_TIME,
): string => {
  const visitDate = transaction.visitDate || transaction.accountDate;
  if (transaction.visitDate && !transaction.visitTime) return transaction.visitDate;
  const visitMinutes = minutes(transaction.visitTime || transaction.accountTime);
  const dayChange = minutes(dayChangeTime || DEFAULT_DAY_CHANGE_TIME) ?? 5 * 60;
  return visitMinutes !== null && visitMinutes < dayChange ? addDays(visitDate, -1) : visitDate;
};

export const airRegiAccountHour = (
  transaction: AirRegiTransactionTime,
  dayChangeTime: string | null = DEFAULT_DAY_CHANGE_TIME,
): number | null => {
  const accountMinutes = minutes(transaction.accountTime);
  if (accountMinutes === null) return null;
  return daysBetween(airRegiBusinessDate(transaction, dayChangeTime), transaction.accountDate) * 24
    + Math.floor(accountMinutes / 60);
};

export const airRegiService = (
  transaction: AirRegiTransactionTime,
  store: ServiceHours | undefined,
): 'lunch' | 'dinner' | 'other' => {
  if (!store) return 'other';
  const dinnerStart = minutes(store.dinnerStart);
  if (dinnerStart === null) return 'other';
  const dayChange = minutes(store.airregiBusinessDayStart || DEFAULT_DAY_CHANGE_TIME) ?? 5 * 60;
  const dinnerEnd = aggregationMinutes(store.dinnerAggregationEnd) ?? 1_440 + dayChange;
  const lunchEnd = aggregationMinutes(store.lunchAggregationEnd) ?? dinnerStart;
  const visitMinutes = minutes(transaction.visitTime);
  const visitCalendarDate = transaction.visitDate || transaction.accountDate;
  const businessDate = airRegiBusinessDate(transaction, store.airregiBusinessDayStart);
  const accountMinutes = minutes(transaction.accountTime);
  if (visitMinutes === null && accountMinutes === null) return 'other';
  const time = visitMinutes === null
    ? accountMinutes! + daysBetween(businessDate, transaction.accountDate) * 1_440
    : visitMinutes + daysBetween(businessDate, visitCalendarDate) * 1_440;
  if (time >= dinnerStart && time < dinnerEnd) return 'dinner';
  const lunchStart = minutes(store.lunchStart);
  return lunchStart !== null && time >= lunchStart && time < lunchEnd ? 'lunch' : 'other';
};
